/**
 * GP HTL (GP HTL Ventures LLC) React Hooks
 * Hooks for fetching and managing GPHTL partnership data
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { API_URL } from '../utils/config';
import {
  GphtlDashboardData,
  GphtlDeal,
  GphtlPaymentChain,
  GphtlEquityMilestone,
  GphtlRevenueSummary,
  GphtlRevenuePeriod,
  GphtlActivity,
  GphtlDealsFilters,
  GphtlPaymentChainFilters,
  GphtlRevenueFilters,
  PaginatedResponse,
  GphtlEquityStatus,
  GphtlEquityLedgerResponse,
  MilestoneVerificationResponse,
} from '../types/gphtl.types';

interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Hook to fetch GPHTL dashboard data
 */
export const useGphtlDashboard = () => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<GphtlDashboardData, Error>({
    queryKey: ['gphtl', 'dashboard'],
    queryFn: async (): Promise<GphtlDashboardData> => {
      if (accounts.length === 0) {
        throw new Error('Not authenticated');
      }

      const response = await authenticatedFetch(`${API_URL}/api/gphtl/dashboard`, {
        timeout: 30000,
      });

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch GPHTL dashboard: ${response?.status || 'No response'}`);
      }

      // API returns dashboard data directly (not wrapped in ApiResponse envelope)
      const result: GphtlDashboardData = await response.json();

      if (!result) {
        throw new Error('No data returned');
      }

      return result;
    },
    enabled: accounts.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
};

/**
 * Hook to fetch GPHTL deals with pagination and filtering
 */
export const useGphtlDeals = (filters?: GphtlDealsFilters, page = 1, pageSize = 25) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<PaginatedResponse<GphtlDeal>, Error>({
    queryKey: ['gphtl', 'deals', filters, page, pageSize],
    queryFn: async (): Promise<PaginatedResponse<GphtlDeal>> => {
      if (accounts.length === 0) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());

      if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters?.dateTo) params.append('dateTo', filters.dateTo);
      if (filters?.stage) params.append('stage', filters.stage);
      if (filters?.owner) params.append('owner', filters.owner);
      if (filters?.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
      if (filters?.invoiceStatus) params.append('invoiceStatus', filters.invoiceStatus);
      if (filters?.search) params.append('search', filters.search);

      const response = await authenticatedFetch(
        `${API_URL}/api/gphtl/deals?${params.toString()}`,
        { timeout: 30000 }
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch GPHTL deals: ${response?.status || 'No response'}`);
      }

      const result = await response.json();
      return {
        data: result.deals || [],
        totalCount: result.totalCount || 0,
        page: result.page || page,
        pageSize: result.pageSize || pageSize,
      };
    },
    enabled: accounts.length > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Hook to fetch GPHTL payment chains with pagination and filtering
 */
export const useGphtlPaymentChains = (filters?: GphtlPaymentChainFilters, page = 1, pageSize = 25) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<PaginatedResponse<GphtlPaymentChain>, Error>({
    queryKey: ['gphtl', 'payment-chains', filters, page, pageSize],
    queryFn: async (): Promise<PaginatedResponse<GphtlPaymentChain>> => {
      if (accounts.length === 0) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());

      if (filters?.status) params.append('status', filters.status);
      if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters?.dateTo) params.append('dateTo', filters.dateTo);
      if (filters?.search) params.append('search', filters.search);

      const response = await authenticatedFetch(
        `${API_URL}/api/gphtl/payment-chains?${params.toString()}`,
        { timeout: 30000 }
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch GPHTL payment chains: ${response?.status || 'No response'}`);
      }

      const result = await response.json();
      return {
        data: result.chains || [],
        totalCount: result.totalCount || 0,
        page: result.page || page,
        pageSize: result.pageSize || pageSize,
      };
    },
    enabled: accounts.length > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Hook to fetch GPHTL payment chain detail for a specific deal
 */
export const useGphtlPaymentChainDetail = (dealId: string | undefined) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<GphtlPaymentChain, Error>({
    queryKey: ['gphtl', 'payment-chain', dealId],
    queryFn: async (): Promise<GphtlPaymentChain> => {
      if (accounts.length === 0 || !dealId) {
        throw new Error('Not authenticated or no deal ID');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/gphtl/payment-chains/${dealId}`,
        { timeout: 30000 }
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch payment chain detail: ${response?.status || 'No response'}`);
      }

      const result = await response.json();
      return result.chain;
    },
    enabled: accounts.length > 0 && !!dealId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Hook to fetch GPHTL revenue summary
 */
export const useGphtlRevenueSummary = () => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<GphtlRevenueSummary, Error>({
    queryKey: ['gphtl', 'revenue', 'summary'],
    queryFn: async (): Promise<GphtlRevenueSummary> => {
      if (accounts.length === 0) {
        throw new Error('Not authenticated');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/gphtl/revenue/summary`,
        { timeout: 30000 }
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch revenue summary: ${response?.status || 'No response'}`);
      }

      // API returns revenue summary directly (not wrapped)
      const result: GphtlRevenueSummary = await response.json();
      return result;
    },
    enabled: accounts.length > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Hook to fetch GPHTL revenue by period
 */
export const useGphtlRevenueByPeriod = (filters?: GphtlRevenueFilters) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<GphtlRevenuePeriod[], Error>({
    queryKey: ['gphtl', 'revenue', 'by-period', filters],
    queryFn: async (): Promise<GphtlRevenuePeriod[]> => {
      if (accounts.length === 0) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams();
      if (filters?.groupBy) params.append('groupBy', filters.groupBy);
      if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters?.dateTo) params.append('dateTo', filters.dateTo);

      const response = await authenticatedFetch(
        `${API_URL}/api/gphtl/revenue/by-period?${params.toString()}`,
        { timeout: 30000 }
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch revenue by period: ${response?.status || 'No response'}`);
      }

      // API returns { data: [...] } format
      const result = await response.json();
      return result.data || [];
    },
    enabled: accounts.length > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Hook to fetch GPHTL equity milestones
 */
export const useGphtlEquityMilestones = () => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<{ milestones: GphtlEquityMilestone[]; totalEarned: number; totalPossible: number }, Error>({
    queryKey: ['gphtl', 'equity', 'milestones'],
    queryFn: async () => {
      if (accounts.length === 0) {
        throw new Error('Not authenticated');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/gphtl/equity/milestones`,
        { timeout: 30000 }
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch equity milestones: ${response?.status || 'No response'}`);
      }

      const result = await response.json();
      return {
        milestones: result.milestones || [],
        totalEarned: result.totalEarned || 0,
        totalPossible: result.totalPossible || 24.5,
      };
    },
    enabled: accounts.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - milestones change less frequently
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  });
};

/**
 * Hook to fetch recent GPHTL activity
 */
export const useGphtlRecentActivity = (limit = 20) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<GphtlActivity[], Error>({
    queryKey: ['gphtl', 'activity', limit],
    queryFn: async (): Promise<GphtlActivity[]> => {
      if (accounts.length === 0) {
        throw new Error('Not authenticated');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/gphtl/activity?limit=${limit}`,
        { timeout: 30000 }
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch recent activity: ${response?.status || 'No response'}`);
      }

      const result = await response.json();
      return result.activities || [];
    },
    enabled: accounts.length > 0,
    staleTime: 1 * 60 * 1000, // 1 minute - activity is more dynamic
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

/**
 * Hook to fetch comprehensive GPHTL equity status with all calculations
 */
export const useGphtlEquityStatus = () => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<GphtlEquityStatus, Error>({
    queryKey: ['gphtl', 'equity', 'status'],
    queryFn: async (): Promise<GphtlEquityStatus> => {
      if (accounts.length === 0) {
        throw new Error('Not authenticated');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/gphtl/equity/status`,
        { timeout: 30000 }
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch equity status: ${response?.status || 'No response'}`);
      }

      const result: GphtlEquityStatus = await response.json();
      return result;
    },
    enabled: accounts.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - equity status changes infrequently
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  });
};

/**
 * Hook to fetch GPHTL equity ledger with optional date filtering
 */
export const useGphtlEquityLedger = (dateFrom?: string, dateTo?: string) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();

  return useQuery<GphtlEquityLedgerResponse, Error>({
    queryKey: ['gphtl', 'equity', 'ledger', dateFrom, dateTo],
    queryFn: async (): Promise<GphtlEquityLedgerResponse> => {
      if (accounts.length === 0) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      const url = params.toString()
        ? `${API_URL}/api/gphtl/equity/ledger?${params.toString()}`
        : `${API_URL}/api/gphtl/equity/ledger`;

      const response = await authenticatedFetch(url, { timeout: 30000 });

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch equity ledger: ${response?.status || 'No response'}`);
      }

      const result: GphtlEquityLedgerResponse = await response.json();
      return result;
    },
    enabled: accounts.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  });
};

/**
 * Hook to verify a milestone (mutation)
 */
export const useVerifyMilestone = () => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<
    MilestoneVerificationResponse,
    Error,
    { milestoneId: number; notes?: string }
  >({
    mutationFn: async ({ milestoneId, notes }) => {
      if (accounts.length === 0) {
        throw new Error('Not authenticated');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/gphtl/equity/milestones/${milestoneId}/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ notes }),
          timeout: 30000,
        }
      );

      if (!response || !response.ok) {
        const errorData = await response?.json().catch(() => ({}));
        throw new Error(
          errorData?.message || `Failed to verify milestone: ${response?.status || 'No response'}`
        );
      }

      const result: MilestoneVerificationResponse = await response.json();
      return result;
    },
    onSuccess: () => {
      // Invalidate relevant queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['gphtl', 'equity'] });
      queryClient.invalidateQueries({ queryKey: ['gphtl', 'dashboard'] });
    },
  });
};
